export interface CurvePoint {
  x: number;
  y: number;
}

/**
 * Fits a natural cubic spline through the given points and returns an
 * evaluator. Unlike a straight-line (linear regression) fit, the curve
 * passes exactly through every point and bends smoothly between them, so
 * reading it at two nearby x values gives two nearby results — no jump at a
 * boundary the way stepping between discrete buckets would.
 *
 * Points sharing an x value are deduplicated first (the last one wins),
 * since a spline is a function — one y per x. x outside the fitted range is
 * clamped to the nearest end rather than extrapolated, since a cubic spline
 * can swing wildly past the data it was built from. Returns null with no
 * points at all; a single point degenerates to a constant, and two points to
 * a straight line — both correct, since there's no curvature to find yet.
 */
export const fitSmoothCurve = (points: CurvePoint[]): ((x: number) => number) | null => {
  const byX = new Map<number, number>();
  points.forEach(p => byX.set(p.x, p.y));
  const sorted = Array.from(byX.entries()).sort((a, b) => a[0] - b[0]);
  if (sorted.length === 0) return null;

  const x = sorted.map(p => p[0]);
  const y = sorted.map(p => p[1]);
  const n = x.length;

  if (n === 1) {
    const constant = y[0];
    return () => constant;
  }

  // Standard natural-cubic-spline solve: second derivatives (c) via a
  // tridiagonal system with "natural" boundary conditions (zero curvature at
  // both ends), then the per-segment cubic coefficients (b, c, d) from those.
  const h = Array.from({ length: n - 1 }, (_, i) => x[i + 1] - x[i]);

  const alpha = new Array(n).fill(0);
  for (let i = 1; i < n - 1; i++) {
    alpha[i] = (3 / h[i]) * (y[i + 1] - y[i]) - (3 / h[i - 1]) * (y[i] - y[i - 1]);
  }

  const l = new Array(n).fill(1);
  const mu = new Array(n).fill(0);
  const z = new Array(n).fill(0);
  for (let i = 1; i < n - 1; i++) {
    l[i] = 2 * (x[i + 1] - x[i - 1]) - h[i - 1] * mu[i - 1];
    mu[i] = h[i] / l[i];
    z[i] = (alpha[i] - h[i - 1] * z[i - 1]) / l[i];
  }

  const c = new Array(n).fill(0);
  const b = new Array(n - 1).fill(0);
  const d = new Array(n - 1).fill(0);
  for (let j = n - 2; j >= 0; j--) {
    c[j] = z[j] - mu[j] * c[j + 1];
    b[j] = (y[j + 1] - y[j]) / h[j] - (h[j] * (c[j + 1] + 2 * c[j])) / 3;
    d[j] = (c[j + 1] - c[j]) / (3 * h[j]);
  }

  return (xq: number) => {
    const clamped = Math.min(Math.max(xq, x[0]), x[n - 1]);
    let i = 0;
    while (i < n - 2 && clamped > x[i + 1]) i++;
    const dx = clamped - x[i];
    return y[i] + b[i] * dx + c[i] * dx * dx + d[i] * dx * dx * dx;
  };
};

/** Solves a 3x3 linear system via Gaussian elimination with partial pivoting. */
const solve3x3 = (a: number[][], rhs: number[]): number[] | null => {
  const m = a.map((row, i) => [...row, rhs[i]]);
  for (let col = 0; col < 3; col++) {
    let pivot = col;
    for (let row = col + 1; row < 3; row++) {
      if (Math.abs(m[row][col]) > Math.abs(m[pivot][col])) pivot = row;
    }
    if (Math.abs(m[pivot][col]) < 1e-10) return null;
    [m[col], m[pivot]] = [m[pivot], m[col]];
    for (let row = 0; row < 3; row++) {
      if (row === col) continue;
      const factor = m[row][col] / m[col][col];
      for (let k = col; k <= 3; k++) m[row][k] -= factor * m[col][k];
    }
  }
  return [m[0][3] / m[0][0], m[1][3] / m[1][1], m[2][3] / m[2][2]];
};

/**
 * Least-squares quadratic (y = ax^2 + bx + c) through the given points — the
 * smooth "expected" bowl-shaped trend a sheet's curl naturally follows across
 * its width (less in the middle, more toward the edges), with no per-call
 * tuning knob the way a moving-average baseline would need.
 *
 * Subtracting this baseline from the actual per-position data is what
 * "flattens" a curl trend line: the broad, gradual physical trend is exactly
 * what a quadratic captures, so it cancels out, while a sharp, narrow anomaly
 * (a flat spot, a wide-curling patch) is too abrupt for a quadratic to
 * absorb and survives as a visible peak or valley in the residual.
 */
export const fitQuadraticBaseline = (points: CurvePoint[]): ((x: number) => number) | null => {
  if (points.length === 0) return null;

  const mean = points.reduce((sum, p) => sum + p.y, 0) / points.length;
  if (points.length < 3) {
    // Too few points to fix a quadratic's 3 coefficients — a flat line at
    // the mean is still the correct "no discernible broad trend" baseline.
    return () => mean;
  }

  let sx = 0, sx2 = 0, sx3 = 0, sx4 = 0, sy = 0, sxy = 0, sx2y = 0;
  for (const { x, y } of points) {
    const x2 = x * x;
    sx += x; sx2 += x2; sx3 += x2 * x; sx4 += x2 * x2;
    sy += y; sxy += x * y; sx2y += x2 * y;
  }
  const n = points.length;
  const solved = solve3x3(
    [[sx4, sx3, sx2], [sx3, sx2, sx], [sx2, sx, n]],
    [sx2y, sxy, sy],
  );
  if (!solved) return () => mean;

  const [a, b, c] = solved;
  return (x: number) => a * x * x + b * x + c;
};
